// A landing subject's digest, as the server makes it (queue/store.rs,
// subject_digest): the SHA-256 of the subject's JSON as serde_json writes it,
// compact, with its fields in the model's order (commit, tree, proposals,
// policy_version, evidence) and its two sets sorted. A person's confirmation
// names that digest, so the page computes it from the subject it shows, and
// sends nothing when it differs from the one the server asks for.
//
// The ids are lowercase hyphenated UUIDs and the shas lowercase hex: their
// string order is the byte order of the server's BTreeSet, and none needs
// escaping in JSON.
import type { LandingSubject } from '../api/types'

/** The subject's JSON, byte for byte as the server serialises it. */
export function subjectJson(subject: LandingSubject): string {
  const sorted = (ids: readonly string[]) => [...new Set(ids)].sort()
  return JSON.stringify({
    commit: subject.commit,
    tree: subject.tree,
    proposals: sorted(subject.proposals),
    policy_version: subject.policy_version,
    evidence: sorted(subject.evidence),
  })
}

/** The subject's SHA-256, in 64 lowercase hexadecimal digits: what `approve_landing`'s confirmation names. */
export async function subjectDigest(subject: LandingSubject): Promise<string> {
  const bytes = new TextEncoder().encode(subjectJson(subject))
  const digest = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', bytes))
  return [...digest].map((b) => b.toString(16).padStart(2, '0')).join('')
}
